import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogIn, Mail, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { AuthLayout } from '../components/auth/AuthLayout';

const LoginPage = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  if (user) {
    navigate('/dashboard', { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore di autenticazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Accesso" subtitle="Inserisci le tue credenziali">
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
        {error && (
          <div className="bg-[#E3000F]/10 border border-[#E3000F]/30 p-3 flex items-center gap-2" data-testid="login-error">
            <AlertCircle className="w-4 h-4 text-[#E3000F] flex-shrink-0" />
            <p className="text-sm text-[#E3000F]">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-xs uppercase font-mono text-[#71717A] mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-[#121212] border border-[#27272A] focus:border-[#E3000F] text-white pl-10 pr-4 py-3 text-sm outline-none"
                placeholder="email@esempio.it"
                required
                data-testid="login-email-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase font-mono text-[#71717A] mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full bg-[#121212] border border-[#27272A] focus:border-[#E3000F] text-white pl-10 pr-10 py-3 text-sm outline-none"
                placeholder="••••••••"
                required
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-white"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-[#E3000F] hover:bg-[#B8000C] disabled:opacity-50 text-white py-3 font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-colors"
          data-testid="login-form-submit-button"
        >
          <LogIn className="w-4 h-4" />
          {loading ? 'ACCESSO...' : 'ACCEDI'}
        </button>

        <div className="flex flex-col items-center gap-2 text-sm">
          <Link to="/forgot-password" className="text-[#E3000F] hover:underline">
            Password dimenticata?
          </Link>
          <Link to="/register" className="text-[#71717A] hover:text-white transition-colors">
            Non hai un account? <span className="text-[#E3000F]">Registrati</span>
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
};

export default LoginPage;
